/**
 * Il payload di lancio (`start_param`) che deve SOPRAVVIVERE al giro di login.
 *
 * ═══ IL DIFETTO CHE QUESTO FILE ESISTE PER CHIUDERE ═══
 * Un link `t.me/<bot>?startapp=inv_bg_<CODICE>` consegna il payload nei launch
 * params di Telegram. Ma su MOBILE l'autenticazione passa dal bridge PKCE:
 * browser esterno → `/auth/callback` → deep link `t.me/<bot>?startapp=auth_<code>`
 * → **Telegram RILANCIA la mini app**, e i launch params del rilancio portano
 * `auth_<code>`. Il payload originale non e' "in fondo alla coda": non esiste
 * piu', perche' e' stato SOSTITUITO.
 *
 * 📌 [M 06/09, bundle servito da stg-tma — la sonda usava `bg_…`, il prefisso
 * di allora: il valore resta quello MISURATO, non lo riscrivo] Un ospite non
 * autenticato che apre
 * `?startapp=bg_QK7M2NPR4T` arriva davvero fin dentro l'app — la console stampa
 * `[bridge] consume: startParam is not auth_*: bg_QK7M2NPR4T` — e l'app lo
 * SCARTA, perche' l'unico lettore di `start_param` e' il bridge di auth, che
 * accetta solo il prefisso `auth_`. L'ospite resta su `/`.
 * ⇒ Il payload arrivava e veniva buttato PRIMA ancora del login. Il login poi
 *   lo sovrascriveva. Due perdite distinte, entrambe chiuse qui.
 *
 * ═══ PERCHE' LO STASH SOPRAVVIVE ALL'INVITO (e non e' una svista) ═══
 * L'invito lato QGammon vive 15 minuti (TTL Redis). Questo stash ne vive 30.
 * La differenza e' VOLUTA: se lo stash scadesse insieme all'invito, l'ospite che
 * ci mette piu' di un quarto d'ora atterrerebbe sulla Hub senza sapere perche' —
 * cioe' esattamente il fallimento silenzioso che il deep link doveva evitare.
 * Sopravvivendo, la schermata di atterraggio puo' dire «questo invito e'
 * scaduto», che e' una risposta. Un vuoto non lo e'.
 *
 * ⚠️ Qui NON si decide nulla sull'invito: si conserva una stringa. Chi la
 * consuma (`usePendingInvite`) naviga; chi atterra (`InviteLanding`) chiede al
 * server se quel codice vale ancora. Questo file non sa se un codice sia buono,
 * e non deve saperlo.
 */

/** Chiave dello stash. Fuori da `PER_SERVICE_TOKEN_KEYS`: non e' un segreto e
 * non deve morire con la sessione — anzi, deve sopravviverle, perche' il caso
 * d'uso e' proprio «non ero loggato quando ho aperto il link». */
const PENDING_KEY = 'qlash.pending_start_param';

/** 30 minuti: il doppio del TTL dell'invito, per la ragione spiegata sopra. */
const STASH_TTL_MS = 30 * 60 * 1000;

/**
 * 🔴 SCHEMA DEL PAYLOAD — `<tipo>_<resto>`, e questo e' L'UNICO punto in cui si
 * decide. Cambiarlo qui cambia tutto, per costruzione.
 *
 * ⚠️ IN ATTESA DI RATIFICA (or52 «deeplink-orc», proposta inviata il 06/09).
 * Questo fix e' il PRIMO consumatore non-`auth_` di `start_param` in tutta la
 * TMA — le due sheet di invito dicono ancora «No deep-link / startapp
 * plumbing» — quindi la forma scelta qui diventa la forma di tutti i deep link
 * futuri (schede torneo, news, eventi), che sono di or52 e non miei.
 *
 * Perche' il TIPO viene prima del gioco (`inv_bg_` e non `bg_`): il primo
 * segmento e' l'unica cosa che il dispatcher deve leggere. Mettendo il gioco in
 * testa, il registro dei tipi diventerebbe un registro dei GIOCHI, e una scheda
 * torneo o una news non ci starebbero dentro.
 *
 * Charset: Telegram ammette `[A-Za-z0-9_-]` in `startapp` (max 64). `inv_bg_` +
 * 10 caratteri di codice = 17: nessuna codifica, come per `auth_`.
 */
const INVITE_PREFIX = 'inv_bg_';

/**
 * Stesso alfabeto che `AcceptInviteSheet.normaliseCode` gia' accetta: `A-Z2-9`,
 * 10 caratteri. Volutamente NON piu' stretto del vicino, anche se il server
 * genera da un alfabeto che esclude pure I e O: un codice che l'utente puo'
 * digitare a mano e uno che arriva da un link devono essere validi insieme,
 * altrimenti si creano due nozioni di «codice valido» che divergono in silenzio.
 */
const CODE_RE = /^[A-Z2-9]{10}$/;

export interface InvitePayload {
  kind: 'invite';
  /** Oggi solo QGammon. Il campo esiste perche' il prefisso lo distingue gia'. */
  game: 'qgammon';
  code: string;
}

interface StashedEntry {
  raw: string;
  savedAt: number;
}

/**
 * Riconosce un payload di lancio. Ritorna `null` per tutto cio' che non e' un
 * invito — compreso `auth_…`, che appartiene al bridge e NON va toccato qui.
 */
export function parseStartParam(raw: string | null | undefined): InvitePayload | null {
  if (!raw || !raw.startsWith(INVITE_PREFIX)) return null;
  const code = raw.slice(INVITE_PREFIX.length).toUpperCase();
  if (!CODE_RE.test(code)) return null;
  return { kind: 'invite', game: 'qgammon', code };
}

/** Costruisce il payload da un codice invito (l'inverso di `parseStartParam`). */
export function inviteStartParam(code: string): string {
  return `${INVITE_PREFIX}${code.toUpperCase()}`;
}

/**
 * Mette da parte il payload, se ne e' uno. Va chiamata AL BOOTSTRAP, prima di
 * qualunque render e quindi prima che l'utente possa premere «Sign in»: e' quel
 * PRIMA a essere la correzione, non il salvataggio in se'.
 *
 * Ritorna cio' che ha salvato (o `null`), cosi' il chiamante puo' asserirlo.
 */
export function stashPendingStartParam(raw: string | null | undefined): InvitePayload | null {
  const parsed = parseStartParam(raw);
  if (!parsed) return null;
  try {
    const entry: StashedEntry = { raw: raw as string, savedAt: Date.now() };
    localStorage.setItem(PENDING_KEY, JSON.stringify(entry));
  } catch {
    // localStorage non disponibile (private mode, storage bloccato): il deep
    // link degrada al comportamento di prima, non rompe il lancio.
    return null;
  }
  return parsed;
}

/** Legge lo stash senza consumarlo. `null` se assente, scaduto o illeggibile. */
export function peekPendingStartParam(): InvitePayload | null {
  let rawEntry: string | null = null;
  try {
    rawEntry = localStorage.getItem(PENDING_KEY);
  } catch {
    return null;
  }
  if (!rawEntry) return null;
  let entry: StashedEntry;
  try {
    entry = JSON.parse(rawEntry) as StashedEntry;
  } catch {
    clearPendingStartParam();
    return null;
  }
  if (!entry || typeof entry.savedAt !== 'number' || Date.now() - entry.savedAt > STASH_TTL_MS) {
    clearPendingStartParam();
    return null;
  }
  return parseStartParam(entry.raw);
}

/** Legge lo stash E lo cancella. Un payload si consuma una volta sola. */
export function consumePendingStartParam(): InvitePayload | null {
  const parsed = peekPendingStartParam();
  clearPendingStartParam();
  return parsed;
}

export function clearPendingStartParam(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* niente da fare: lo stash scade comunque da solo */
  }
}
